/**
 * Interface for messages that can be topologically sorted by reply relationships.
 */
export interface TopologicallySortableMessage {
  id: string;
  sentAt: Date;
  inReplyToId: string | null;
}

/**
 * Sorts messages by sentAt, with topological ordering for equal timestamps.
 * Within each timestamp group, reply chains are kept together (parent followed
 * by children in a depth-first manner). Only reply relationships within the
 * same timestamp group are considered for ordering.
 */
export function sortMessagesByReplyChain<
  T extends TopologicallySortableMessage,
>(messages: readonly T[]): T[] {
  if (messages.length <= 1) {
    return [...messages];
  }

  // Group messages by timestamp
  const byTimestamp = new Map<number, T[]>();
  for (const msg of messages) {
    const key = msg.sentAt.getTime();
    if (!byTimestamp.has(key)) {
      byTimestamp.set(key, []);
    }
    byTimestamp.get(key)!.push(msg);
  }

  // Sort timestamp keys ascending
  const sortedTimestamps = [...byTimestamp.keys()].sort((a, b) => a - b);

  const result: T[] = [];

  for (const timestamp of sortedTimestamps) {
    const group = byTimestamp.get(timestamp)!;

    // Build lookup for messages in this timestamp group only
    const groupById = new Map(group.map((m) => [m.id, m]));

    // Build parent->children map (only for relationships within this group)
    const children = new Map<string, T[]>();
    const roots: T[] = [];

    for (const msg of group) {
      // Parent must be in this same timestamp group to count as a reply relationship
      if (msg.inReplyToId && groupById.has(msg.inReplyToId)) {
        if (!children.has(msg.inReplyToId)) {
          children.set(msg.inReplyToId, []);
        }
        children.get(msg.inReplyToId)!.push(msg);
      } else {
        roots.push(msg);
      }
    }

    // Sort roots by id for stability
    roots.sort((a, b) => a.id.localeCompare(b.id));

    // Sort each children list by id for stability
    for (const childList of children.values()) {
      childList.sort((a, b) => a.id.localeCompare(b.id));
    }

    // Depth-first traversal to keep chains together
    const visited = new Set<string>();

    const visit = (msg: T): void => {
      if (visited.has(msg.id)) {
        return; // Cycle protection
      }
      visited.add(msg.id);
      result.push(msg);

      const msgChildren = children.get(msg.id) || [];
      for (const child of msgChildren) {
        visit(child);
      }
    };

    for (const root of roots) {
      visit(root);
    }

    // Handle cycles: any unvisited messages are part of cycles with no root
    // Sort them by id and add them
    const unvisited = group.filter((m) => !visited.has(m.id));
    unvisited.sort((a, b) => a.id.localeCompare(b.id));
    for (const msg of unvisited) {
      visit(msg);
    }
  }

  return result;
}
